import Vue from 'vue';
import moment from 'moment';
import { AbstractModel, ActivityDateModel } from '.';
import { isTodayDate, updateDateWithTime, formatDateWithTime } from '../services/dates';

class model extends AbstractModel {
    endpoint = 'activity';

    loadUpcomingQuests = () => {
        const limit = 3;
        const promises = [];
        if (!Vue.$store.state.organization.selectedOrganizationID) {
            return Promise.resolve([]);
        }
        promises.push(
            this.get({
                params: {
                    'response-group': 'upcomming_quests',
                    limit: limit,
                    filter: [
                        {
                            type: 'gte',
                            field: 'startDate',
                            value: moment()
                                .add(1, 'day')
                                .set({ h: 0, m: 0, s: 0 })
                                .format('YYYY-MM-DD HH:mm:ss'),
                        },
                        {
                            type: 'eq',
                            field: 'isActive',
                            value: true,
                        },
                        {
                            type: 'neq',
                            field: 'activityType',
                            value: 'periodic',
                        },
                        {
                            type: 'eq',
                            field: 'organization',
                            value: Vue.$store.state.organization.selectedOrganizationID,
                        },
                    ],
                    'order-by': [
                        {
                            field: 'startDate',
                            direction: 'ASC',
                            type: 'field',
                        },
                    ],
                },
            })
        );
        promises.push(
            ActivityDateModel.get({
                params: {
                    'response-group': 'upcomming_quests',
                    limit: limit,
                    filter: [
                        {
                            type: 'gte',
                            field: 'date',
                            value: moment()
                                .add(1, 'day')
                                .set({ h: 0, m: 0, s: 0 })
                                .format('YYYY-MM-DD'),
                        },
                        {
                            type: 'eq',
                            field: 'isActive',
                            value: true,
                        },
                        {
                            type: 'eq',
                            field: 'organization',
                            value: Vue.$store.state.organization.selectedOrganizationID,
                        },
                    ],
                    'order-by': [
                        {
                            field: 'date',
                            direction: 'ASC',
                            type: 'field',
                        },
                    ],
                },
            })
        );
        return Promise.all(promises).then(result => {
            const datesByQuests = [];
            const [{ data: basicQuests }, { data: periodicDates }] = result;

            for (const basicQuest of basicQuests) {
                const startDate = moment(basicQuest.startDate.date);
                datesByQuests.push({
                    activityId: basicQuest.id,
                    date: formatDateWithTime(startDate),
                    dateId: null,
                });
            }

            for (const periodicDate of periodicDates) {
                const startDate = moment(periodicDate.date.date);
                const startDateWithTime = updateDateWithTime(periodicDate.startTime, startDate);
                datesByQuests.push({
                    activityId: periodicDate.activity.id,
                    date: startDateWithTime,
                    dateId: periodicDate.id,
                });
            }

            const activitiesToRender = datesByQuests
                .sort((a, b) => {
                    if (a.date > b.date) {
                        return 1;
                    } else if (a.date < b.date) {
                        return -1;
                    } else {
                        return 0;
                    }
                })
                .slice(0, limit);

            const activitiesId = [];

            activitiesToRender.forEach(activity => {
                activitiesId.push(activity.activityId);
            });

            return this.get({
                params: {
                    'response-group': 'org_backend_dashboard',
                    filter: [
                        {
                            type: 'in',
                            field: 'id',
                            values: activitiesId,
                        },
                    ],
                },
            })
                .then(r => r.data)
                .then(activities => {
                    const result = [];
                    for (const activityToRender of activitiesToRender) {
                        for (const activity of activities) {
                            if (activity.id == activityToRender.activityId) {
                                if (activityToRender.dateId) {
                                    const dates = activity.activityDates.filter(
                                        date => date.id == activityToRender.dateId
                                    );
                                    result.push({
                                        ...activity,
                                        activityDates: dates,
                                    });
                                } else {
                                    result.push(activity);
                                }
                            }
                        }
                    }
                    return result;
                });
        });
    };

    loadCurrentDashboardQuests = () => {
        return this.get({
            params: {
                limit: -1,
                'response-group': 'org_backend_dashboard',
                filter: [
                    {
                        type: 'lt',
                        field: 'startDate',
                        value: moment()
                            .set({ h: 23, m: 59, s: 59 })
                            .format('YYYY-MM-DD HH:mm:ss'),
                    },
                    {
                        type: 'gt',
                        field: 'endDate',
                        value: moment()
                            .set({ h: 0, m: 0, s: 0 })
                            .format('YYYY-MM-DD HH:mm:ss'),
                    },
                    {
                        type: 'eq',
                        field: 'isActive',
                        value: true,
                    },
                    {
                        type: 'eq',
                        field: 'activeTodayOnly',
                        value: true,
                    },
                    {
                        type: 'eq',
                        field: 'organization',
                        value: Vue.$store.state.organization.selectedOrganizationID,
                    },
                ],
            },
        })
            .then(r => r.data)
            .then(quests => {
                return this.orderQuests(quests);
            });
    };

    getLoadQuestsListPromises = ({
        signal,
        activeQuestLoader,
        draftQuestLoader,
        pastQuestLoader,
        skipQuestGroupFilter
    }) => {
        const promises = [];
        if (typeof activeQuestLoader === 'function') {
            activeQuestLoader(false);
        }
        if (typeof draftQuestLoader === 'function') {
            draftQuestLoader(false);
        }
        if (typeof pastQuestLoader === 'function') {
            pastQuestLoader(false);
        }
        const additionalFilters = [];
        if (!skipQuestGroupFilter && Vue.$store.state.questList.activityGroup) {
            additionalFilters.push({
                type: 'eq',
                field: 'activityGroup',
                value: Vue.$store.state.questList.activityGroup.id,
            });
        }
        promises.push(
            Vue.$store
                .dispatch('questList/loadQuests', {
                    signal,
                    group: 'activeQuests',
                    filters: [
                        {
                            type: 'gt',
                            field: 'endDate',
                            format: 'd.m.Y H:i:s',
                            value: moment().format('DD.MM.YYYY HH:mm:ss'),
                        },
                        {
                            type: 'eq',
                            field: 'isActive',
                            value: true,
                        },
                        ...additionalFilters,
                    ],
                    orderBy: [
                        {
                            field: 'startDate',
                            direction: 'DESC',
                            type: 'field',
                        },
                    ],
                })
                .then(() => {
                    if (typeof activeQuestLoader === 'function') {
                        activeQuestLoader(true);
                    }
                })
        );

        promises.push(
            Vue.$store
                .dispatch('questList/loadQuests', {
                    signal,
                    group: 'draftQuests',
                    filters: [
                        {
                            type: 'eq',
                            field: 'isActive',
                            value: false,
                        },
                        ...additionalFilters,
                    ],
                    orderBy: [
                        {
                            field: 'startDate',
                            direction: 'DESC',
                            type: 'field',
                        },
                    ],
                })
                .then(() => {
                    if (typeof draftQuestLoader === 'function') {
                        draftQuestLoader(true);
                    }
                })
        );

        promises.push(
            Vue.$store
                .dispatch('questList/loadQuests', {
                    signal,
                    group: 'pastQuests',
                    filters: [
                        {
                            type: 'lt',
                            field: 'endDate',
                            format: 'd.m.Y H:i:s',
                            value: moment().format('DD.MM.YYYY HH:mm:ss'),
                        },
                        {
                            type: 'eq',
                            field: 'isActive',
                            value: true,
                        },
                        ...additionalFilters,
                    ],
                    orderBy: [
                        {
                            field: 'endDate',
                            direction: 'DESC',
                            type: 'field',
                        },
                    ],
                })
                .then(() => {
                    if (typeof pastQuestLoader === 'function') {
                        pastQuestLoader(true);
                    }
                })
        );
        return promises;
    };

    createOnlineQuestHash() {
        if (crypto.subtle) {
            const msgUint8 = new TextEncoder().encode(Date.now());
            return crypto.subtle.digest('SHA-256', msgUint8).then(hashBuffer => {
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
            });
        } else {
            return Promise.resolve(btoa(Date.now()));
        }
    }

    prepareActivitiesStartDate(quests, countParticipants = true, currentDate = true) {
        const result = [];
        const today = moment().format('DD.MM.YYYY');

        let data = {};

        for (const quest of quests) {
            if (!quest.activityDates.length) {
                data = { ...quest };
                if (countParticipants) {
                    data.participantsCount = this.countParticipants(quest);
                }
                result.push(data);
            } else {
                for (const activityDate of quest.activityDates) {
                    const time = moment(activityDate.startTime, 'HH:mm');
                    const date = activityDate.date.date.split(' ')[0];
                    const hours = time.format('HH');
                    const minutes = time.format('mm');
                    const dateString = `${date}T${hours}:${minutes}:00`;
                    const startDate = moment(dateString);
                    if (!currentDate || startDate.format('DD.MM.YYYY') === today) {
                        quest.startDate = {
                            ...activityDate.date,
                            date: startDate.format('YYYY-MM-DDTHH:mmZ'),
                        };
                        if (countParticipants) {
                            quest.participantsCount = this.countParticipants(quest, activityDate);
                        }
                        break;
                    }
                }
                result.push(quest);
            }
        }
        return result;
    }

    changeQuestStatus(id, status) {
        return this.patch(id, {
            body: {
                isActive: status,
            },
            params: {
                'response-group': 'org_backend_quest_details',
            },
        });
    }

    orderQuests(quests) {
        const questsToOrder = {};
        for (const quest of quests) {
            if (quest.activityDates.length > 0) {
                for (const activityDate of quest.activityDates) {
                    if (activityDate.isActive && isTodayDate(activityDate.date.date)) {
                        questsToOrder[quest.id] = updateDateWithTime(activityDate.endTime, activityDate.date.date);
                        break;
                    }
                }
            } else {
                questsToOrder[quest.id] = formatDateWithTime(moment(quest.endDate.date));
            }
        }

        const orderedQuests = Object.keys(questsToOrder).sort((a, b) => {
            const aDate = questsToOrder[a];
            const bDate = questsToOrder[b];
            if (aDate < bDate) {
                return -1;
            } else if (aDate > bDate) {
                return 1;
            } else {
                return 0;
            }
        });

        const result = [];
        for (const orderedQuest of orderedQuests) {
            for (const quest of quests) {
                if (quest.id == orderedQuest) {
                    result.push(quest);
                    break;
                }
            }
        }

        return result;
    }

    countParticipants(quest, activityDate) {
        let result = 0;

        if (activityDate) {
            if (activityDate.participants) {
                const participantsNotToCount = quest.responsibleActivities.map(responsible => {
                    if (!responsible.receivesPoints) {
                        return responsible.participant.id;
                    }
                });
                result += activityDate.participants.filter(
                    participant => !participantsNotToCount.includes(participant.id)
                ).length;
            }
        } else if (quest) {
            if (quest.participantActivities) {
                result += quest.participantActivities.length;
            }
            if (quest.responsibleActivities) {
                result += quest.responsibleActivities.filter(a => a.receivesPoints).length;
            }
        }

        return result;
    }

    getQuestShareEmailSubject(component, quest) {
        return component.$t('Jetzt bei der Quest "[Questname]" mitmachen!').replace('[Questname]', quest.name);
    }

    getQuestShareEmailBody(component, quest) {
        return `${component.$t('Sei dabei und sammle Points. Spannende Rewards und der aha plus Nachweis warten auf dich:')}\r\n${this.getQuestFEUrl(quest)}`;
    }

    getQuestFEUrl(quest) {
        return `${process.env.VUE_APP_FE_HOST}/plus/quests/${quest.id}`;
    }
}

export const ActivityModel = new model();
